import type { AnchorChainId } from '@/features/wallet/config/anchors';
import {
    discoverPasskeyOwner,
    exchangeAccessToken,
    fetchTradingSession,
    finishTradingSession,
    startPasskeyDiscovery,
    startTradingSession,
} from '@/lib/api/tradingSession';
import type {
    AccessTokenResponse,
    FinishTradingSessionRequest,
    PasskeyDiscoveryOptionsResponse,
    PasskeyOwnerDiscoveryResponse,
    SerializedPasskeyAssertionProof,
    SnappyTypedData,
    StartTradingSessionRequest,
    TradingSessionDurationMinutes,
    TradingSessionFinishResponse,
    TradingSessionStartResponse,
    TradingSessionStatus,
    TradingSessionWindowSeconds,
} from '@/lib/api/tradingSession';
import { hasErrorCode, isApiError } from '@/lib/errors';

import { getPasskeyAssertionWithFriendlyError } from './passkeyAssertion';
import {
    clearPasskeyOwnerHint,
    readPasskeyOwnerHint,
    writePasskeyOwnerHint,
} from './passkeyOwnerHint';
import {
    assertCanonicalTradingSessionChallenge,
    assertPasskeyDiscoveryChallenge,
    assertTradingSessionSigningPayload,
} from './protocol';
import {
    resolveSessionAnchor,
    type SessionAnchorResolution,
    type TradingSessionIdentity,
} from './walletResolution';
import {
    getPasskeyAssertion,
    type PublicKeyCredentialRequestOptionsJSON,
    type SerializedAssertionCredential,
} from './webauthn';

type AnchoredCeremonyInput = {
    /**
     * The connected wallet's chain, as a TIE-BREAK for the automatic anchor
     * resolution when the owner holds wallets on both anchors. Never a choice
     * by itself: an anchor with no wallet is never picked because of it.
     */
    preferredAnchorChainId?: number | null;
};
type EipCeremonyInput = AnchoredCeremonyInput & {
    method: 'eip712';
    durationMinutes: TradingSessionDurationMinutes;
    wallet: string;
    signTypedData: (typedData: SnappyTypedData) => Promise<string>;
};
type PasskeyCeremonyInput = AnchoredCeremonyInput & {
    method: 'passkey';
    durationMinutes: TradingSessionDurationMinutes;
    /**
     * Owner already known to the caller (e.g. the expiring session's owner
     * during reauthentication). Skips the extra discovery assertion — the
     * "whose passkey is this?" WebAuthn prompt — exactly like the persisted
     * owner hint does. A wrong value fails closed (`passkeyNotFound` →
     * {@link PasskeyOwnerHintStaleError}), never signs for the wrong owner.
     */
    knownOwner?: string;
};
export type TradingSessionCeremonyInput =
    | EipCeremonyInput
    | PasskeyCeremonyInput;

interface CeremonyDependencies {
    resolveAnchor: (
        owner: string,
        preferredChainId?: number | null,
    ) => Promise<SessionAnchorResolution>;
    start: (
        request: StartTradingSessionRequest,
    ) => Promise<TradingSessionStartResponse>;
    finish: (
        request: FinishTradingSessionRequest,
    ) => Promise<TradingSessionFinishResponse>;
    fetchStatus: () => Promise<TradingSessionStatus>;
    exchangeToken: () => Promise<AccessTokenResponse>;
    startDiscovery: () => Promise<PasskeyDiscoveryOptionsResponse>;
    discoverOwner: (
        assertion: SerializedPasskeyAssertionProof,
    ) => Promise<PasskeyOwnerDiscoveryResponse>;
    getAssertion: (
        options: PublicKeyCredentialRequestOptionsJSON,
    ) => Promise<SerializedAssertionCredential>;
    readOwnerHint: () => `0x${string}` | null;
    writeOwnerHint: (owner: string) => void;
    clearOwnerHint: () => void;
}

const defaultDependencies: CeremonyDependencies = {
    resolveAnchor: resolveSessionAnchor,
    start: startTradingSession,
    finish: finishTradingSession,
    fetchStatus: fetchTradingSession,
    exchangeToken: exchangeAccessToken,
    startDiscovery: startPasskeyDiscovery,
    discoverOwner: discoverPasskeyOwner,
    getAssertion: getPasskeyAssertion,
    readOwnerHint: readPasskeyOwnerHint,
    writeOwnerHint: writePasskeyOwnerHint,
    clearOwnerHint: clearPasskeyOwnerHint,
};

export interface CompletedTradingSessionCeremony {
    session: TradingSessionFinishResponse;
    status: TradingSessionStatus;
    appToken: AccessTokenResponse;
}

type ActiveTradingSessionStatus = Extract<
    TradingSessionStatus,
    { state: 'active' }
>;

export class TradingSessionCookieUnavailableError extends Error {
    readonly code = 'tradingSessionCookieUnavailable';

    constructor() {
        super(
            'Your browser blocked the secure trading cookie, so the trading window could not start.',
        );
        this.name = 'TradingSessionCookieUnavailableError';
    }
}

export class PasskeyOwnerHintStaleError extends Error {
    readonly code = 'passkeyOwnerHintStale';

    constructor() {
        super(
            'This passkey does not belong to the saved account. Try Passkey again to rediscover it.',
        );
        this.name = 'PasskeyOwnerHintStaleError';
    }
}

export function isTradingSessionCookieUnavailableError(
    error: unknown,
): error is TradingSessionCookieUnavailableError {
    return (
        error instanceof TradingSessionCookieUnavailableError ||
        hasErrorCode(error, 'tradingSessionCookieUnavailable')
    );
}

export function isPasskeyOwnerHintStaleError(
    error: unknown,
): error is PasskeyOwnerHintStaleError {
    return (
        error instanceof PasskeyOwnerHintStaleError ||
        hasErrorCode(error, 'passkeyOwnerHintStale')
    );
}

/** The server's per-anchor refusal: the proof did not verify on THIS anchor. */
function isAnchorSpecificSessionRefusal(error: unknown): boolean {
    return isApiError(error) && error.body?.code === 'sessionInvalid';
}

function windowSeconds(
    durationMinutes: TradingSessionDurationMinutes,
): TradingSessionWindowSeconds {
    return (durationMinutes * 60) as TradingSessionWindowSeconds;
}

export async function completeTradingSessionCeremony(
    input: TradingSessionCeremonyInput,
    dependencies: CeremonyDependencies = defaultDependencies,
): Promise<CompletedTradingSessionCeremony> {
    // The anchor is DERIVED, not asked for: the one whose manager holds this
    // owner's wallet. See resolveSessionAnchor for the tie-break rules.
    if (input.method === 'passkey') {
        const hintedOwner = input.knownOwner ?? dependencies.readOwnerHint();
        let discoveredCredentialId: string | null = null;
        let owner: string;
        if (hintedOwner) {
            owner = hintedOwner;
        } else {
            const discovery = await dependencies.startDiscovery();
            assertPasskeyDiscoveryChallenge(discovery.publicKey.challenge);
            const assertion = await getPasskeyAssertionWithFriendlyError(
                dependencies.getAssertion,
                discovery.publicKey,
            );
            const discovered = await dependencies.discoverOwner(
                serializeAssertion(assertion),
            );
            owner = discovered.owner;
            discoveredCredentialId = assertion.rawId;
        }

        const resolved = await dependencies.resolveAnchor(
            owner,
            input.preferredAnchorChainId,
        );
        const runPasskeyRound = async (
            anchorChainId: AnchorChainId,
            identity: TradingSessionIdentity,
        ): Promise<CompletedTradingSessionCeremony> => {
            const started = await dependencies.start({
                method: 'passkey',
                ...identity,
                windowSeconds: windowSeconds(input.durationMinutes),
                anchorChainId,
            });
            if (started.method !== 'passkey') {
                throw new Error(
                    'The server returned the wrong trading-session method.',
                );
            }
            assertCanonicalPasskeyIdentity(started.statement, identity);
            if (started.publicKey.challenge !== started.challenge) {
                throw new Error(
                    'The server returned mismatched passkey challenge options.',
                );
            }
            await assertCanonicalTradingSessionChallenge(
                started.statement,
                started.challenge,
            );

            const assertion = await getPasskeyAssertionWithFriendlyError(
                dependencies.getAssertion,
                discoveredCredentialId
                    ? {
                        ...started.publicKey,
                        allowCredentials: [
                            {
                                id: discoveredCredentialId,
                                type: 'public-key',
                            },
                        ],
                    }
                    : started.publicKey,
            );
            let session: TradingSessionFinishResponse;
            try {
                session = await dependencies.finish({
                    method: 'passkey',
                    statement: started.statement,
                    ...serializeAssertion(assertion),
                });
            } catch (error) {
                if (
                    hintedOwner &&
                isApiError(error) &&
                error.body?.code === 'passkeyNotFound'
                ) {
                    dependencies.clearOwnerHint();
                    throw new PasskeyOwnerHintStaleError();
                }
                throw error;
            }
            if (session.method !== 'passkey') {
                throw new Error(
                    'The server returned the wrong trading-session method.',
                );
            }
            if (session.owner.toLowerCase() !== owner.toLowerCase()) {
                throw new Error(
                    'The server returned a trading session for a different owner.',
                );
            }

            const status = await requireActiveSession(
                input.method,
                started.statement,
                dependencies,
            );
            const appToken = await dependencies.exchangeToken();
            assertSignedSessionCorrelation(
                appToken,
                started.statement,
                'access token',
                hasCompleteCorrelation(status),
            );
            dependencies.writeOwnerHint(owner);
            return { session, status, appToken };
        };
        try {
            return await runPasskeyRound(
                resolved.anchorChainId,
                resolved.identity,
            );
        } catch (error) {
            // A passkey is registered on exactly ONE anchor's wallet, and an
            // owner holding wallets on both gives the automatic pick a 50%
            // guess. The server's refusal is anchor-specific (sessionInvalid),
            // so one silent retry on the other anchor - at the cost of one
            // more passkey prompt - signs where the passkey actually lives.
            if (
                resolved.alternate &&
                isAnchorSpecificSessionRefusal(error)
            ) {
                return runPasskeyRound(
                    resolved.alternate.anchorChainId,
                    resolved.alternate.identity,
                );
            }
            throw error;
        }
    }

    const { anchorChainId, identity } = await dependencies.resolveAnchor(
        input.wallet,
        input.preferredAnchorChainId,
    );
    const started = await dependencies.start({
        method: 'eip712',
        ...identity,
        windowSeconds: windowSeconds(input.durationMinutes),
        anchorChainId,
    });
    if (started.method !== 'eip712') {
        throw new Error('The server returned the wrong trading-session method.');
    }

    assertTradingSessionSigningPayload(
        started.eip712,
        started.statement,
        identity.owner,
        anchorChainId,
    );
    const signature = await input.signTypedData(started.eip712);
    const session = await dependencies.finish({
        method: 'eip712',
        statement: started.statement,
        signature,
    });
    if (
        session.method !== 'eip712' ||
        !sameAddress(session.owner, started.statement.owner)
    ) {
        throw new Error(
            'The server returned a trading session for a different owner.',
        );
    }
    const status = await requireActiveSession(
        input.method,
        started.statement,
        dependencies,
    );
    const appToken = await dependencies.exchangeToken();
    assertSignedSessionCorrelation(
        appToken,
        started.statement,
        'access token',
        hasCompleteCorrelation(status),
    );
    return { session, status, appToken };
}

function serializeAssertion(
    assertion: SerializedAssertionCredential,
): SerializedPasskeyAssertionProof {
    return {
        credentialId: assertion.rawId,
        authenticatorData: assertion.response.authenticatorData,
        clientDataJson: assertion.response.clientDataJSON,
        signature: assertion.response.signature,
    };
}

async function requireActiveSession(
    method: TradingSessionCeremonyInput['method'],
    statement: TradingSessionStartResponse['statement'],
    dependencies: CeremonyDependencies,
): Promise<ActiveTradingSessionStatus> {
    const status = await dependencies.fetchStatus();
    if (
        status.state !== 'active' ||
        status.method !== method ||
        typeof status.expiresAtMs !== 'number'
    ) {
        throw new TradingSessionCookieUnavailableError();
    }
    assertSignedSessionCorrelation(status, statement, 'cookie status');
    return status;
}

function sameAddress(left: string, right: string): boolean {
    return left.toLowerCase() === right.toLowerCase();
}

function assertCanonicalPasskeyIdentity(
    statement: TradingSessionStartResponse['statement'],
    identity: TradingSessionIdentity,
): void {
    if (
        !sameAddress(statement.owner, identity.owner) ||
        !sameAddress(
            statement.authorizationWallet,
            identity.authorizationWallet,
        ) ||
        !sameAddress(statement.tradingWallet, identity.tradingWallet)
    ) {
        throw new Error(
            'The server returned a canonical passkey statement for a different wallet identity.',
        );
    }
}

function assertSignedSessionCorrelation(
    value: { owner?: string; sessionId?: string },
    statement: TradingSessionStartResponse['statement'],
    source: string,
    required = false,
): void {
    const owner = value.owner;
    const sessionId = value.sessionId;
    if (owner === undefined && sessionId === undefined && !required) return;
    if (
        owner === undefined ||
        sessionId === undefined ||
        !sameAddress(owner, statement.owner) ||
        sessionId.toLowerCase() !== statement.sessionId.toLowerCase()
    ) {
        throw new Error(
            `The server returned a different signed session in the ${source}.`,
        );
    }
}

function hasCompleteCorrelation(value: {
    owner?: string;
    sessionId?: string;
}): value is { owner: string; sessionId: string } {
    return (
        typeof value.owner === 'string' && typeof value.sessionId === 'string'
    );
}
